/**
 * QRScannerDialog — Iter-169 · Phase 1.
 *
 * Full-screen QR scanner wired to the tenant dashboard. Uses the
 * html5-qrcode library that already ships in the frontend bundle.
 * Handles camera permission denial gracefully with a clear prompt.
 *
 * When a QR payload starts with `necs://` we treat it as an in-app
 * deep-link and route via `useNavigate`. External URLs open in a new
 * tab. Anything else is echoed back to the parent via `onResult` so
 * the caller can decide what to do (e.g. log it as a scan event).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { AlertCircle, Camera, CheckCircle2, RefreshCw, XCircle } from 'lucide-react';

const SCANNER_ELEMENT_ID = 'necs-qr-scanner-region';

export default function QRScannerDialog({ open, onOpenChange, onResult }) {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const [status, setStatus] = useState('idle'); // idle | starting | scanning | denied | error | result
  const [errMsg, setErrMsg] = useState('');
  const [result, setResult] = useState(null);

  const stop = useCallback(async () => {
    try {
      if (scannerRef.current && scannerRef.current.isScanning) {
        await scannerRef.current.stop();
      }
    } catch (_) {}
    try { if (scannerRef.current) await scannerRef.current.clear(); } catch (_) {}
    scannerRef.current = null;
  }, []);

  const handleDecoded = useCallback(async (decoded) => {
    if (!decoded || status === 'result') return;
    setResult(decoded);
    setStatus('result');
    await stop();
    if (typeof onResult === 'function') {
      try { onResult(decoded); } catch (_) {}
    }
    // Route on scan payload.
    if (typeof decoded === 'string') {
      if (decoded.startsWith('necs://')) {
        const path = decoded.replace('necs://', '/');
        onOpenChange?.(false);
        navigate(path);
      } else if (/^https?:\/\//i.test(decoded)) {
        window.open(decoded, '_blank', 'noopener,noreferrer');
      }
    }
  }, [status, stop, onResult, navigate, onOpenChange]);

  const start = useCallback(async () => {
    setStatus('starting');
    setErrMsg('');
    setResult(null);
    try {
      const el = document.getElementById(SCANNER_ELEMENT_ID);
      if (!el) throw new Error('Scanner region not mounted yet');
      const instance = new Html5Qrcode(SCANNER_ELEMENT_ID, { verbose: false });
      scannerRef.current = instance;
      await instance.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        handleDecoded,
        () => {}, // per-frame parse failures — ignore
      );
      setStatus('scanning');
    } catch (e) {
      const raw = (e && (e.message || e.toString())) || 'Camera failed to start';
      if (/permission|NotAllowed|denied/i.test(raw)) {
        setStatus('denied');
        setErrMsg(raw);
      } else {
        setStatus('error');
        setErrMsg(raw);
      }
      await stop();
    }
  }, [handleDecoded, stop]);

  useEffect(() => {
    if (open) {
      // Delay one tick so the DOM region is mounted before we start
      // html5-qrcode against it.
      const t = setTimeout(start, 80);
      return () => clearTimeout(t);
    }
    // Closed — always stop the camera.
    stop();
    setStatus('idle');
    setResult(null);
    setErrMsg('');
    return undefined;
  }, [open, start, stop]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="qr-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Camera className="h-5 w-5 text-[#C9A962]" /> Scan QR Code
          </DialogTitle>
        </DialogHeader>

        {status === 'denied' ? (
          <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4 space-y-2"
               data-testid="qr-scanner-permission-denied">
            <div className="flex items-start gap-2">
              <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-bold text-red-900">Camera access blocked</p>
                <p className="text-red-800 mt-1 leading-snug">
                  We need camera permission to scan QR codes. Open your device
                  <strong> Settings → Privacy → Camera</strong> (iOS) or
                  <strong> Settings → Apps → NECS → Permissions</strong> (Android)
                  and allow access, then reopen the scanner.
                </p>
              </div>
            </div>
            <Button onClick={start} className="w-full mt-2"
                    data-testid="qr-scanner-retry">
              <RefreshCw className="h-4 w-4 mr-2" /> Try Again
            </Button>
          </div>
        ) : status === 'error' ? (
          <div className="rounded-lg border-2 border-orange-300 bg-orange-50 p-4 space-y-2"
               data-testid="qr-scanner-error">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-orange-900">
                Scanner failed to start.{errMsg ? ` ${errMsg}` : ''}
              </p>
            </div>
            <Button onClick={start} className="w-full mt-2"
                    data-testid="qr-scanner-retry">
              <RefreshCw className="h-4 w-4 mr-2" /> Try Again
            </Button>
          </div>
        ) : status === 'result' ? (
          <div className="rounded-lg border-2 border-green-300 bg-green-50 p-4 space-y-2"
               data-testid="qr-scanner-result">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm min-w-0">
                <p className="font-bold text-green-900">Scan complete</p>
                <p className="text-green-800 mt-1 break-all">{String(result)}</p>
              </div>
            </div>
            <Button onClick={start} variant="outline" className="w-full mt-2"
                    data-testid="qr-scanner-scan-again">
              <Camera className="h-4 w-4 mr-2" /> Scan Another
            </Button>
          </div>
        ) : (
          <>
            <div id={SCANNER_ELEMENT_ID}
                 className="w-full rounded-lg overflow-hidden bg-black"
                 style={{ minHeight: 260 }}
                 data-testid="qr-scanner-region" />
            <p className="text-xs text-zinc-500 mt-2 text-center"
               data-testid="qr-scanner-hint">
              Point your camera at the code. Hold steady until it decodes.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
